// @ts-strict-ignore
import { isArray } from 'lodash';
import { computed, observable, makeObservable } from 'mobx';

import { SymptomTicketUrgency } from 'constants/urgency.consts';

import {
  DepressionScreeningAnswer,
  ProcessedDepressionData,
  determinePHQType,
  hasSuicidalIdeation,
  calculatePHQScore,
  getSuicidalIdeationResponse,
  PHQType,
  DepressionScreenStep
} from 'models/DepressionScreening';
import { OralReportVersion, ReportQuestionAnswer } from 'models/OralReport/OralReport.shared';
import { OralReportV1 } from 'models/OralReport/OralReportV1';
import { OralReportV2 } from 'models/OralReport/OralReportV2';
import { OralReportV3 } from 'models/OralReport/OralReportV3';
import { ReportedCause } from 'models/ReportedCause';

export enum ReportType {
  Distress = 'distress',
  Oral = 'oral',
  COVID = 'covid', // To be deprecated
  CallbackRequestTickets = 'callbackRequestTickets',
  DrugSpecific = 'drugSpecific',
  DepressionScreening = 'depressionScreening'
}

export enum ReportSource {
  Mobile = 'App',
  Ivr = 'IVR',
  Web = 'WebApp'
}

export interface IReportAnswer {
  version?: OralReportVersion.V2 | OralReportVersion.V3;
  causes?: ReportedCause[];
  distressLevel?: number;
  oral?: OralReportV1 | OralReportV2 | OralReportV3;
  callbackRequest?: ReportQuestionAnswer<boolean>;
  answers?: DepressionScreeningAnswer[]; // Raw depression screening answers
  // deprecated
  inputQuestions?: any;
  yesNoQuestions?: any;
}

export default class QuestionnaireAnswer {
  @observable id: number;

  @observable patientId: number;

  @observable type: ReportType;

  @observable answer: IReportAnswer;

  @observable createdAt: string;

  @observable patientReportIndex: number;

  @observable ticketId: number;

  @observable source: ReportSource;

  @observable urgency: SymptomTicketUrgency | null;

  isUnacknowledged = (lastReportAckTime: string) =>
    !lastReportAckTime || new Date(this.createdAt) > new Date(lastReportAckTime);

  isAfter = (date: Date) => new Date(this.createdAt) > date;

  constructor() {
    makeObservable(this);
  }

  // Used for SymptomReport wrapper in DistressReportsTable, remove if component was refactored
  get isOperatorReport() {
    return false;
  }

  get callbackDenyReasonId(): number {
    // Mobile clients send the additions data differently, remove ambiguity after fixed on mobile clients
    return isArray(this.answer.callbackRequest.additions)
      ? this.answer.callbackRequest.additions[0]
      : this.answer.callbackRequest.additions;
  }

  @computed
  get name() {
    switch (this.type) {
      case ReportType.Distress:
        return 'Symptom';
      case ReportType.Oral:
        return 'Oral Oncolytics';
      case ReportType.COVID:
        return 'COVID-19';
      case ReportType.DrugSpecific:
        return 'Drug Specific';
      case ReportType.DepressionScreening:
        return 'Depression Screening';
      default:
        throw new Error('Unknown Report Type');
    }
  }

  @computed
  get depressionData(): ProcessedDepressionData | null {
    if (this.type !== ReportType.DepressionScreening) {
      return null;
    }

    // Depression screening data is always stored in answer.answers
    if (!Array.isArray(this.answer.answers)) {
      return null;
    }

    const { answers } = this.answer;
    const phqType = determinePHQType(answers);
    const totalScore = calculatePHQScore(answers);
    const hasSuicidalIdeationFlag = hasSuicidalIdeation(answers);
    const suicidalIdeationResponse = getSuicidalIdeationResponse(answers);

    const responses = answers.map((answer) => ({
      questionText: answer.additions.questionTitle,
      responseText: answer.additions.optionTitle,
      score: answer.value,
      isSuicidalIdeationQuestion: answer.questionType === DepressionScreenStep.Question9
    }));

    return {
      phqType,
      totalScore,
      hasSuicidalIdeation: hasSuicidalIdeationFlag,
      suicidalIdeationResponse,
      responses
    };
  }

  @computed
  get phqScore(): number {
    return this.depressionData?.totalScore || 0;
  }

  @computed
  get phqType(): PHQType | null {
    return this.depressionData?.phqType || null;
  }

  @computed
  get hasSuicidalIdeation(): boolean {
    return this.depressionData?.hasSuicidalIdeation || false;
  }
}

export interface IReportedYesNoQuestion {
  id: number;
  questionText: string;
  value: boolean;
}

export interface IReportedInputQuestion {
  id: number;
  questionText: string;
  inputType: string;
  answer: any;
}
